// The album — ports app/(privado)/album/page.tsx.
//
// The `?album=` filter was a server-side searchParam; it's now read with
// useSearchParams, which means switching folders no longer round-trips.
//
// "+ Nova foto" abre o formulário de envio aqui mesmo, com a pasta aberta já
// escolhida — como o mapa e o calendário fazem. O álbum "Conto" é a exceção:
// as suas imagens continuam a ser geridas na página do Conto.

import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { CONTO_ALBUM_ID } from "@historia/shared/contracts";
import { emptyStates } from "@historia/shared/content";
import { Flourish } from "@/components/ui/Flourish";
import { CapasAlbuns } from "@/components/album/CapasAlbuns";
import { FormFoto } from "@/components/album/FormFoto";
import { GrelhaAlbum } from "@/components/album/GrelhaAlbum";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";
import {
  useAlbums,
  useAlbumsWithCounts,
  useIllustrations,
  useMilestoneSummary,
  usePhotos,
  usePlaces,
} from "@/api/queries";

export function Album() {
  useTitle("O álbum");

  const [params] = useSearchParams();
  const albumId = params.get("album") ?? undefined;
  const eConto = albumId === CONTO_ALBUM_ID;

  const [aAdicionar, setAAdicionar] = useState(false);

  // The API resolves the "conto" sentinel itself, mapping illustrations into
  // photo shape — so one query covers both cases.
  const query = usePhotos(albumId);
  const capas = useAlbumsWithCounts();
  const albuns = useAlbums();
  const marcos = useMilestoneSummary();
  const ilustracoes = useIllustrations();
  const lugares = usePlaces();

  const albumInfo = albuns.data?.find((a) => a.id === albumId);
  const filtroAtivo = eConto ? CONTO_ALBUM_ID : albumInfo ? albumId! : null;
  const titulo = eConto ? "Imagens do Conto" : albumInfo ? albumInfo.nome : "O álbum de memórias";

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <header className="text-center">
        <p className="font-display text-lg italic text-ouro-600">O nosso baú</p>
        <h1 className="mt-2 font-display text-4xl text-tinta">{titulo}</h1>
        <div className="my-6">
          <Flourish />
        </div>
      </header>

      <CapasAlbuns
        albuns={capas.data ?? []}
        ativo={filtroAtivo}
        contagemConto={ilustracoes.data?.length ?? 0}
      />

      {eConto ? (
        <div className="mb-8 text-center">
          <Link
            to="/conto/adicionar"
            className="inline-block rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600"
          >
            + Nova ilustração
          </Link>
        </div>
      ) : aAdicionar ? (
        <div className="mb-8">
          <FormFoto
            // `key` limpa o formulário ao mudar de pasta enquanto está aberto.
            key={filtroAtivo ?? "geral"}
            marcos={marcos.data ?? []}
            albuns={albuns.data ?? []}
            albumInicial={filtroAtivo ?? undefined}
            aoTerminar={() => setAAdicionar(false)}
          />
        </div>
      ) : (
        <div className="mb-8 text-center">
          <button
            type="button"
            onClick={() => setAAdicionar(true)}
            className="rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 shadow-[0_10px_28px_-10px_rgba(201,162,39,0.65)] transition-all hover:-translate-y-0.5 hover:bg-ouro-600"
          >
            + Nova foto
          </button>
        </div>
      )}

      <Consulta query={query}>
        {(fotos) =>
          fotos.length === 0 ? (
            <div className="mt-10 text-center">
              <p className="mx-auto max-w-prose leading-relaxed text-tinta-700">
                {eConto
                  ? "Ainda não há imagens no Conto. Adiciona ilustrações na página do Conto."
                  : filtroAtivo
                    ? "Este álbum ainda não tem fotos. Adiciona algumas aqui, ou move-as a partir do álbum geral."
                    : emptyStates.album}
              </p>
            </div>
          ) : (
            <div className="mt-4">
              <GrelhaAlbum
                fotos={fotos}
                marcos={marcos.data ?? []}
                albuns={albuns.data ?? []}
                lugares={lugares.data ?? []}
                somenteLeitura={eConto}
              />
            </div>
          )
        }
      </Consulta>
    </main>
  );
}
